/*
 * Validates a ?redirect= target before it is navigated to after login.
 *
 * Two things go wrong with an unchecked value:
 *
 *   - A protocol-relative path ("//chats") is resolved by the browser against
 *     the scheme, not this app, so it becomes a different origin. React Router
 *     hands it to history.pushState, which refuses a cross-origin URL and
 *     throws a SecurityError. Thrown from inside the login handler's try block,
 *     that surfaced as "Login failed" on a login that had actually succeeded.
 *     "?redirect=%2F/chats?threadId=12" decodes to exactly that, because only
 *     "&" separates query parameters - a "?" inside a value is part of it.
 *
 *   - An absolute URL ("https://example.com") would send a freshly signed-in
 *     user straight off the site, which is an open redirect.
 *
 * So the only thing accepted is a path inside this app: one leading slash, and
 * nothing that a browser would read as a host.
 */
const DEFAULT_TARGET = '/dashboard';

export const safeRedirect = (raw, fallback = DEFAULT_TARGET) => {
  if (!raw) return fallback;

  // URLSearchParams.get() has already decoded once; this second pass matches
  // what the login pages did before, for links that were encoded twice.
  let value = String(raw);
  try {
    value = decodeURIComponent(value);
  } catch (e) {
    // Malformed escapes - keep what we were given and let the checks below rule.
  }

  value = value.trim();

  if (!value.startsWith('/')) return fallback; // absolute URL, or a bare word
  if (value === '/') return fallback; // the guard sends "/" for the root
  // "//host" is protocol-relative; browsers normalise "/\" to the same thing.
  if (value.startsWith('//') || value.startsWith('/\\')) return fallback;

  return value;
};

export default safeRedirect;
